import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Container, Row } from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ConfirmDeleteModal from "../../../Components/Common/ConfirmDeleteModal";
import TableContainer from "../../../Components/Common/TableContainer";
import {
  useDeleteUserSubcategory,
  useUserSubcategories,
} from "../../../hooks/useUserCategories";

/**
 * Institution list.
 *
 * An institution is the precise entity ("Ministère de la Justice") inside an
 * institution category ("Entreprise étatique"). Nothing existed to manage them
 * before: only a read endpoint was wired, so they could only be created
 * straight in the database.
 */
const SubCategories = (props) => {
  document.title = props.t("Institutions | Le Conseiller du BTP");

  const subcategoriesQ = useUserSubcategories();
  const subcategories = subcategoriesQ.data || [];
  const deleteMutation = useDeleteUserSubcategory();

  const [subcategory, setSubcategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const errorMessage =
    typeof subcategoriesQ.error === "string"
      ? subcategoriesQ.error
      : subcategoriesQ.error?.message || props.t("Une erreur est survenue");

  const onClickDelete = (item) => {
    setSubcategory(item);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    if (!subcategory) return;
    deleteMutation.mutate(subcategory.id, {
      onSuccess: () => setDeleteModal(false),
    });
  };

  // An institution with users attached cannot be deleted: the FK would reject
  // it, so the modal blocks the action and explains what to do instead.
  const deleteBlockedReason = useMemo(() => {
    const usersCount = subcategory?.users_count || 0;
    if (usersCount === 0) return null;

    return `${usersCount} utilisateur${
      usersCount > 1 ? "s sont rattachés" : " est rattaché"
    } à cette institution. Rattachez-${
      usersCount > 1 ? "les" : "le"
    } à une autre institution avant de la supprimer.`;
  }, [subcategory]);

  const columns = useMemo(
    () => [
      {
        Header: props.t("INSTITUTION"),
        accessor: "name",
        Cell: (cell) => (
          <p className="mb-1 fs-14 fw-medium">{cell.row.original.name}</p>
        ),
      },
      {
        Header: props.t("CATEGORIE D'INSTITUTIONS"),
        accessor: "category_name",
        Cell: (cell) => (
          <span className="badge bg-primary-subtle text-primary fs-12">
            {cell.row.original.category_name || "-"}
          </span>
        ),
      },
      {
        Header: props.t("UTILISATEURS"),
        accessor: "users_count",
        Cell: (cell) => (
          <span className="badge bg-light text-body fs-12">
            {cell.row.original.users_count || 0}
          </span>
        ),
      },
      {
        Header: props.t("ACTIONS"),
        Cell: (cellProps) => {
          const item = cellProps.row.original;
          return (
            <div className="gap-2 d-flex">
              <Link
                to={`/utilisateurs-institutions-update/${item.id}/`}
                className="text-warning d-inline-block"
                title={props.t("Modifier")}
              >
                <i className="align-bottom ri-pencil-fill" />
              </Link>
              <Link
                to="#"
                className="text-danger d-inline-block"
                title={props.t("Supprimer")}
                onClick={(e) => {
                  e.preventDefault();
                  onClickDelete(item);
                }}
              >
                <i className="align-bottom ri-delete-bin-5-line" />
              </Link>
            </div>
          );
        },
      },
    ],
    [props],
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <ConfirmDeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        title={props.t("Supprimer cette institution ?")}
        itemLabel={subcategory?.name}
        implications={[
          props.t(
            "les utilisateurs ne pourront plus être rattachés à cette institution",
          ),
        ]}
        blockedReason={deleteBlockedReason}
        isPending={deleteMutation.isPending}
      />

      <Container fluid>
        <BreadCrumb
          title={props.t("Institutions")}
          pageTitle={props.t("Utilisateurs")}
        />

        <Row>
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              <div className="pt-0 card-body">
                <div className="mt-3 mb-0 alert alert-info" role="alert">
                  <i className="align-bottom ri-information-line me-1"></i>
                  Une <strong>sous-catégorie d&apos;institutions</strong>{" "}
                  précise l&apos;appartenance d&apos;un utilisateur à
                  l&apos;intérieur d&apos;une{" "}
                  <strong>catégorie d&apos;institutions</strong>. Exemple :
                  catégorie «&nbsp;Entreprise étatique&nbsp;», sous-catégorie
                  «&nbsp;Ministère de la Justice&nbsp;».
                </div>

                {subcategoriesQ.isLoading ? (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/xjovhxra.json"
                      trigger="loop"
                      colors="primary:#0960b6,secondary:#02376b"
                      style={{ width: "72px", height: "72px" }}
                    ></lord-icon>
                    <div className="mt-4">
                      <h5>{props.t("Chargement")}...</h5>
                    </div>
                  </div>
                ) : subcategoriesQ.isError ? (
                  <div className="py-4 text-center">
                    <div className="mt-4">
                      <h5>{errorMessage}</h5>
                      <button
                        type="button"
                        className="mt-3 btn btn-outline-primary"
                        onClick={() => subcategoriesQ.refetch()}
                      >
                        <i className="align-bottom ri-refresh-line me-1"></i>
                        {props.t("Réessayer")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={subcategories}
                    isGlobalFilter={true}
                    isAddUserSubcategorie={true}
                    customPageSize={10}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle"
                    theadClass="table-light text-muted"
                  />
                )}
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

SubCategories.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SubCategories));
